import { api } from '@/lib/api';
import type {
  ChannelResponse,
  CreateChannelRequest,
  UpdateChannelRequest,
  ChannelQuery,
  ChannelListResponse,
  ChannelRateLimitsResponse,
  UpdateChannelRateLimitsRequest,
  ChannelConnectionDetails,
  ReconnectChannelRequest,
  ReconnectChannelResult,
  ProviderCapability,
  ExtendedStatus,
} from '@troia-v3/types';
import type { ChannelEmailConfigResponse, UpdateChannelEmailConfigRequest } from '../types';

/** Item de canal na rota `/channels/dropdown`. */
export interface ChannelDropdownItem {
  id: string;
  name: string;
  identifier: string;
  /** Vem da integração vinculada (`company-integrations.providerId`). */
  providerId?: string;
  /** Vem da integração vinculada — usado para renderização condicional. */
  capabilities: ProviderCapability[];
  /**
   * `Channel.status` é `ExtendedStatus` (`active|inactive|pending|suspended|error`),
   * não só `active|inactive` — `markChannelDisconnected` e o health-scheduler
   * gravam `pending`/`error` em produção nos fluxos de desconexão/reconexão do
   * WhatsApp, e o backend (`mapToResponse`) devolve o valor cru nesta mesma rota.
   */
  status: ExtendedStatus;
  createdAt: string;
  updatedAt: string;
}

export const channelsApi = {
  /**
   * Get all channels with pagination and filters
   */
  async getAll(query?: ChannelQuery): Promise<ChannelListResponse> {
    const response = await api.get('/channels', { params: query });
    return response.data.data;
  },

  /**
   * Get single channel by ID
   */
  async getById(id: string): Promise<ChannelResponse> {
    const response = await api.get(`/channels/${id}`);
    return response.data.data;
  },

  /**
   * Create new channel (auto-creates integration)
   */
  async create(data: CreateChannelRequest): Promise<ChannelResponse> {
    const response = await api.post('/channels', data);
    return response.data.data;
  },

  /**
   * Update existing channel
   */
  async update(id: string, data: UpdateChannelRequest): Promise<ChannelResponse> {
    const response = await api.put(`/channels/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete channel (soft delete)
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/channels/${id}`);
  },

  /**
   * Workflows ATIVOS que usam o canal — os mesmos que a pausa automática desliga
   * ao excluir. Rota sem permissão de módulo (dropdown), chamada só no clique de
   * excluir. Falha → lista vazia (o aviso é informativo, nunca bloqueia).
   */
  async getActiveWorkflowsUsingChannel(id: string): Promise<Array<{ id: string; name: string }>> {
    try {
      const response = await api.get<{ success: boolean; data: Array<{ id: string; name: string }> }>(
        `/workflows/channel-references/${id}`,
      );
      return response.data.data ?? [];
    } catch {
      return [];
    }
  },

  /**
   * Activate channel
   */
  async activate(id: string): Promise<ChannelResponse> {
    const response = await api.post(`/channels/${id}/activate`);
    return response.data.data;
  },

  /**
   * Deactivate channel
   */
  async deactivate(id: string): Promise<ChannelResponse> {
    const response = await api.post(`/channels/${id}/deactivate`);
    return response.data.data;
  },

  /**
   * Get active channels only
   */
  async getActive(): Promise<ChannelResponse[]> {
    const response = await api.get('/channels/active');
    return response.data.data;
  },

  /**
   * Get inactive channels only
   */
  async getInactive(): Promise<ChannelResponse[]> {
    const response = await api.get('/channels/inactive');
    return response.data.data;
  },

  /**
   * Count total channels
   */
  async count(): Promise<number> {
    const response = await api.get('/channels/count');
    return response.data.data.count;
  },

  /**
   * Get channels for dropdown — `GET /channels/dropdown` (sem permissionMiddleware).
   *
   * O campo a campo espelha a projeção do backend
   * (`channels/repository.ts` → `findAllForDropdown`): `providerId` e
   * `capabilities` vêm do `$lookup` em `company-integrations`.
   */
  async getForDropdown(): Promise<ChannelDropdownItem[]> {
    const response = await api.get('/channels/dropdown');
    return response.data.data;
  },

  /**
   * Get channels with status for connection monitoring
   * Returns full ChannelResponse including status field
   */
  async getChannelsWithStatus(): Promise<ChannelResponse[]> {
    const response = await api.get('/channels/dropdown');
    return response.data.data;
  },

  /**
   * Get users assigned to a channel
   */
  async getChannelUsers(channelId: string): Promise<Array<{ id: string; name: string; avatar?: string }>> {
    const response = await api.get(`/channels/${channelId}/users`);
    return response.data.data;
  },

  /**
   * Detalhes de conexão (token atual + webhook) para o form de reconexão de provider oficial.
   */
  async getConnectionDetails(id: string): Promise<ChannelConnectionDetails> {
    const response = await api.get(`/channels/${id}/connection-details`);
    return response.data.data;
  },

  /**
   * Reconnect channel. Sem body = gateway QR / teste. Com credentials = renovar token oficial.
   */
  async reconnect(id: string, body?: ReconnectChannelRequest): Promise<ReconnectChannelResult> {
    const response = await api.post(`/channels/${id}/reconnect`, body ?? {});
    return response.data.data;
  },

  /**
   * Switch engine (Node ↔ Go) for a Gateway WhatsApp channel.
   * Destructive: encerra sessão atual e gera novo QR Code na engine de destino.
   */
  async switchEngine(id: string, engine: 'node' | 'go'): Promise<{
    success: boolean;
    qrCode?: string;
    qrCodeExpires?: number;
    instanceKey?: string;
    message?: string;
  }> {
    const response = await api.post(`/channels/${id}/switch-engine`, { engine });
    return response.data.data;
  },

  /**
   * Get rate limit configuration for a channel
   */
  async getRateLimits(channelId: string): Promise<ChannelRateLimitsResponse> {
    const response = await api.get(`/channels/${channelId}/rate-limits`);
    return response.data.data as ChannelRateLimitsResponse;
  },

  /**
   * Update rate limit configuration for a channel
   */
  async updateRateLimits(channelId: string, body: UpdateChannelRateLimitsRequest): Promise<ChannelRateLimitsResponse> {
    const response = await api.patch(`/channels/${channelId}/rate-limits`, body);
    return response.data.data as ChannelRateLimitsResponse;
  },

  /**
   * Restore rate limits to default values
   */
  async restoreRateLimits(channelId: string): Promise<ChannelRateLimitsResponse> {
    const response = await api.delete(`/channels/${channelId}/rate-limits`);
    return response.data.data as ChannelRateLimitsResponse;
  },

  // ============================================================================
  // EMAIL CONFIG (Plano B — Email→Ticket, Task 7). Escopo: apenas
  // provider `email-resend` — backend responde 404 para outros providers.
  // ============================================================================

  /**
   * Get email→ticket configuration for a channel (provider `email-resend`)
   */
  async getEmailConfig(channelId: string): Promise<ChannelEmailConfigResponse> {
    const response = await api.get(`/channels/${channelId}/email-config`);
    return response.data.data as ChannelEmailConfigResponse;
  },

  /**
   * Update email→ticket configuration for a channel. Envia apenas os campos
   * alterados — o backend faz merge por campo. `_handleErrorLocally` evita
   * o toast global duplicado; o caller (hook) mostra a mensagem real do erro.
   */
  async updateEmailConfig(
    channelId: string,
    body: UpdateChannelEmailConfigRequest
  ): Promise<ChannelEmailConfigResponse> {
    const response = await api.patch(
      `/channels/${channelId}/email-config`,
      body,
      { _handleErrorLocally: true } as any
    );
    return response.data.data as ChannelEmailConfigResponse;
  },

  // ============================================================================
  // USER-CHANNEL MANAGEMENT (Operators)
  // ============================================================================

  /**
   * Add user (operator) to channel
   */
  async addUserToChannel(
    channelId: string,
    data: {
      userId: string;
      role: 'viewer' | 'attendant';
      scope?: 'own' | 'team' | 'all';
      priority?: number;
      capabilities?: string[];
    }
  ): Promise<{
    id: string;
    userId: string;
    channelId: string;
    role: 'viewer' | 'attendant';
    scope: 'own' | 'team' | 'all';
    priority: number | null;
    capabilities: string[];
    assignedAt: string;
    status: 'active';
  }> {
    const response = await api.post(`/channels/${channelId}/users`, data);
    return response.data.data;
  },

  /**
   * Remove user (operator) from channel
   */
  async removeUserFromChannel(channelId: string, userId: string): Promise<void> {
    await api.delete(`/channels/${channelId}/users/${userId}`);
  },

  /**
   * Update user role/capabilities in channel
   */
  async updateUserInChannel(
    channelId: string,
    userId: string,
    data: {
      role?: 'viewer' | 'attendant';
      scope?: 'own' | 'team' | 'all';
      priority?: number;
      capabilities?: string[];
    }
  ): Promise<{
    id: string;
    userId: string;
    channelId: string;
    role: 'viewer' | 'attendant';
    scope: 'own' | 'team' | 'all';
    priority: number | null;
    capabilities: string[];
    assignedAt: string;
    status: 'active' | 'inactive';
    updatedAt: string;
  }> {
    const response = await api.patch(`/channels/${channelId}/users/${userId}`, data);
    return response.data.data;
  },

  /**
   * Get available users to add to channel
   */
  async getAvailableUsers(
    channelId: string,
    query?: {
      search?: string;
      role?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    users: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      avatar?: string;
      role: string;
      status: 'active' | 'inactive';
      isInChannel: boolean;
    }>;
    total: number;
    limit: number;
    offset: number;
  }> {
    const response = await api.get(`/channels/${channelId}/available-users`, { params: query });
    return response.data.data;
  },

  /**
   * Get channel operators statistics
   */
  async getChannelOperatorsStats(channelId: string): Promise<{
    total: number;
    byRole: {
      viewer: number;
      attendant: number;
    };
    activeAssignments: number;
    byOperator: Array<{
      userId: string;
      firstName: string;
      lastName: string;
      avatar?: string;
      activeAssignments: number;
      lastActivity?: string;
    }>;
  }> {
    const response = await api.get(`/channels/${channelId}/operators/stats`);
    return response.data.data;
  },

  // ============================================================================
  // ASSIGNMENT TESTING
  // ============================================================================

  /**
   * Test assignment configuration
   */
  async testAssignment(data: {
    channelId: string;
    resourceType: 'customer' | 'lead' | 'project' | 'document' | 'ticket';
    resourceId: string;
    priority?: number;
    metadata?: Record<string, any>;
  }): Promise<{
    success: boolean;
    assignedTo: {
      teamId: string | null;
      userId: string | null;
      reason?: string;
    };
    message: string;
    executedAt: Date;
    details?: Record<string, any>;
  }> {
    const response = await api.post('/channels/test-assignment', data);
    return response.data;
  },

  /**
   * Manual assignment
   */
  async manualAssign(data: {
    channelId: string;
    resourceType: 'customer' | 'lead' | 'project' | 'document' | 'ticket';
    resourceId: string;
    userId: string;
    priority?: number;
    notes?: string;
  }): Promise<{
    success: boolean;
    assignmentId: string;
    message: string;
    assignedAt: Date;
  }> {
    const response = await api.post('/channels/manual-assign', data);
    return response.data;
  },

  // ============================================================================
  // WIDGET MANAGEMENT
  // ============================================================================

  /**
   * Get widget embed script
   */
  async getWidgetEmbedScript(channelId: string): Promise<{
    embedScript: string;
    embedUrl: string;
    socketUrl: string;
    hostedPageUrl?: string;
  }> {
    const response = await api.get(`/widget/embed-script/${channelId}`);
    return response.data.data;
  },

  /**
   * Get widget configuration
   */
  async getWidgetConfig(channelId: string): Promise<{
    channelId: string;
    name: string;
    config: Record<string, any>;
    status: string;
  }> {
    const response = await api.get(`/widget/config/${channelId}`);
    return response.data.data;
  },

  /**
   * Update widget configuration
   */
  async updateWidgetConfig(
    channelId: string,
    config: {
      brandColor?: string;
      position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
      welcomeMessage?: string;
      agentName?: string;
      suggestions?: Array<{ icon: string; text: string }>;
      hostedPage?: {
        enabled: boolean;
        layout: 'centered' | 'fullbleed' | 'split';
        headline?: string;
        subtitle?: string;
        logoUrl?: string;
        backgroundColor?: string;
      };
      features?: {
        allowImages?: boolean;
        allowAudio?: boolean;
        allowDocuments?: boolean;
        allowVideo?: boolean;
        allowPwaInstall?: boolean;
        allowPushNotifications?: boolean;
      };
    }
  ): Promise<ChannelResponse> {
    const response = await api.patch(`/widget/config/${channelId}`, config);
    return response.data.data;
  }
};
